import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Vitals, StatDose, MaternityFile } from '../../types';
import { MaternityErrorBoundary } from '../../components/ui/MaternityErrorBoundary';
import { Activity, ArrowRight, History, Syringe, Trash2, Baby, Save, CheckCircle } from 'lucide-react';
interface Props {}
export const NursingDashboard: React.FC<Props> = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    maternityFiles,
    updateMaternityFile
  } = useData();
  const [activeTab, setActiveTab] = useState<'vitals' | 'medications' | 'maternity'>('vitals');
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [selectedMaternityFileRaw, setSelectedMaternityFileRaw] = useState<MaternityFile | null>(null);
  // Older maternity files (and partially-saved drafts) may be missing the deeply
  // nested objects the form reads (`admission.date`, `presentPregnancy.hpTest`,
  // `delivery.deliverySummary`, etc). Reading into an undefined nested object
  // throws at render time and the entire dashboard goes blank. Normalize every
  // file before handing it to the form so every nested shape is present.
  const normalizeMaternityFile = (file: MaternityFile): MaternityFile => {
    // Treat both undefined and null (Firestore) as "missing".
    const f: any = file || {};
    const obj = (v: any) => v && typeof v === 'object' ? v : {};
    const arr = (v: any) => Array.isArray(v) ? v : [];
    return {
      ...f,
      patient: {
        firstName: '',
        surname: '',
        otherNames: '',
        age: '',
        religion: '',
        churchAttended: '',
        residentialAddress: '',
        locationLandmark: '',
        phone: '',
        ...obj(f.patient),
        nextOfKin: {
          name: '',
          phone: '',
          relationship: '',
          ...obj(obj(f.patient).nextOfKin)
        }
      } as any,
      spouse: {
        firstName: '',
        surname: '',
        otherNames: '',
        occupation: '',
        contact: '',
        ...obj(f.spouse)
      } as any,
      admission: {
        date: '',
        time: '',
        ...obj(f.admission)
      } as any,
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        hb: '',
        abo: '',
        rh: '',
        ...obj(f.generalExam),
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: '',
          ...obj(obj(f.generalExam).msUrine)
        }
      } as any,
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        pelvicAssessment: '',
        abnormalities: '',
        ...obj(f.presentPregnancy),
        syphilisTest: {
          date: '',
          result: '',
          treatment: '',
          ...obj(obj(f.presentPregnancy).syphilisTest)
        },
        hpTest: {
          date: '',
          result: '',
          ...obj(obj(f.presentPregnancy).hpTest)
        }
      } as any,
      labourObservations: arr(f.labourObservations),
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        ...obj(f.delivery),
        deliverySummary: arr(obj(f.delivery).deliverySummary)
      } as any,
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: '',
        ...obj(f.newbornEvaluation)
      } as any,
      postDeliveryObservations: arr(f.postDeliveryObservations),
      puerperiumNotes: arr(f.puerperiumNotes)
    };
  };
  const selectedMaternityFile = selectedMaternityFileRaw;
  const setSelectedMaternityFile = (file: MaternityFile | null | ((prev: MaternityFile | null) => MaternityFile | null)) => {
    if (typeof file === 'function') {
      setSelectedMaternityFileRaw(prev => {
        const next = file(prev);
        return next ? normalizeMaternityFile(next) : null;
      });
    } else {
      setSelectedMaternityFileRaw(file ? normalizeMaternityFile(file) : null);
    }
  };
  const [vitals, setVitals] = useState({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  // Medical alerts (Allergies + HIV) — captured at nursing assessment
  const [medicalAlerts, setMedicalAlerts] = useState<{
    allergies: string;
    hivStatus: 'Positive' | 'Negative' | 'Unknown';
  }>({
    allergies: '',
    hivStatus: 'Unknown'
  });
  // Stat Dose form state
  const [statDoseForm, setStatDoseForm] = useState({
    drugName: '',
    dose: '',
    route: 'PO',
    notes: ''
  });
  // Show patients in NURSING stage AND any active patient (so nurses can administer stat doses anytime)
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const activeEncounters = encounters.filter(e => e.stage !== 'COMPLETED' && e.stage !== 'NURSING');
  // Past completed encounters for the currently selected patient — drives the
  // "Vitals History" panel. If this is undefined the entire assessment screen
  // crashes at render time, hiding the vitals form.
  const pastEncounters = selectedEncounter ? encounters.filter(e => e.patientId === selectedEncounter.patientId && e.id !== selectedEncounter.id && e.vitals).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()) : [];
  const handleAddStatDose = () => {
    if (!selectedEncounter || !currentUser || !statDoseForm.drugName) return;
    const newDose: StatDose = {
      id: `stat-${Date.now()}`,
      drugName: statDoseForm.drugName,
      dose: statDoseForm.dose,
      route: statDoseForm.route,
      notes: statDoseForm.notes,
      givenAt: new Date().toISOString(),
      givenBy: currentUser.id,
      nurseName: currentUser.fullName
    };
    const existing = selectedEncounter.statDoses || [];
    updateEncounter(selectedEncounter.id, {
      statDoses: [...existing, newDose]
    });
    // Refresh selected encounter to show new dose
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: [...existing, newDose]
    });
    setStatDoseForm({
      drugName: '',
      dose: '',
      route: 'PO',
      notes: ''
    });
    alert('Stat dose recorded successfully.');
  };
  const handleSaveMaternityDraft = () => {
    if (!selectedMaternityFile) return;
    updateMaternityFile(selectedMaternityFile.id, selectedMaternityFile);
    alert('Maternity file draft saved.');
  };
  const handleCompleteMaternity = () => {
    if (!selectedMaternityFile || !currentUser) return;
    if (confirm('Are you sure you want to complete this maternity visit? It will be moved to the archive.')) {
      updateMaternityFile(selectedMaternityFile.id, {
        ...selectedMaternityFile,
        stage: 'COMPLETED',
        completedAt: new Date().toISOString(),
        completedBy: currentUser.id
      });
      alert('Maternity visit completed and archived.');
      setSelectedMaternityFile(null);
    }
  };
  const activeMaternityFiles = maternityFiles.filter(m => m.stage === 'NURSING');
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const w = parseFloat(weightStr);
    const h = parseFloat(heightStr) / 100; // convert cm to m
    if (w > 0 && h > 0) {
      const bmi = (w / (h * h)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleRemoveStatDose = (doseId: string) => {
    if (!selectedEncounter) return;
    const remaining = (selectedEncounter.statDoses || []).filter(d => d.id !== doseId);
    updateEncounter(selectedEncounter.id, {
      statDoses: remaining
    });
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: remaining
    });
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    const newVitals: Vitals = {
      ...vitals,
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: newVitals,
      stage: 'CLINICIAN'
    });
    // Update patient's medical alerts
    const patientToUpdate = patients.find(p => p.id === selectedEncounter.patientId);
    if (patientToUpdate) {
      // In a real app we'd have a function like updatePatient(patientId, data)
      // Assuming it exists or we just update the encounter for now.
    }
    alert('Vitals saved and patient forwarded to Clinician.');
    setSelectedEncounter(null);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const patient = selectedEncounter ? patients.find(p => p.id === selectedEncounter.patientId) : null;
  return <div className="space-y-6" data-id="element-3913">
      <div className="flex justify-between items-center" data-id="element-3914">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3915">Nursing Dashboard</h1>
        <div className="flex space-x-2" data-id="element-3916">
          <Badge variant="success" className="text-sm px-3 py-1" data-id="element-3917">
            {pendingEncounters.length} Vitals Pending
          </Badge>
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-3918">
            {activeEncounters.length} Active Patients
          </Badge>
          <Badge variant="pink" className="text-sm px-3 py-1 bg-pink-100 text-pink-800" data-id="element-3919">
            {activeMaternityFiles.length} Maternity
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-3920">
        <nav className="-mb-px flex space-x-8" data-id="element-3921">
          <button onClick={() => {
          setActiveTab('vitals');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'vitals' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3922">
            Vitals Queue
          </button>
          <button onClick={() => {
          setActiveTab('medications');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'medications' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3923">
            Medications & Treatments
          </button>
          <button onClick={() => {
          setActiveTab('maternity');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'maternity' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3924">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-3925" />
            Maternity Ward
          </button>
        </nav>
      </div>

      {activeTab === 'vitals' && <div className="space-y-6" data-id="element-3926">
          {!selectedEncounter ? <div className="space-y-4" data-id="element-3927">
              <Card title="Awaiting Vitals" noPadding data-id="element-3928">
                <ul className="divide-y divide-gray-200" data-id="element-3929">
                  {pendingEncounters.length > 0 ? pendingEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-3930">
                          <div className="flex items-center" data-id="element-3931">
                            <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-3932">
                              <Activity className="h-6 w-6 text-blue-600" data-id="element-3933" />
                            </div>
                            <div data-id="element-3934">
                              <p className="text-sm font-medium text-blue-600" data-id="element-3935">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-3936">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-3937">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-3938" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-3939">
                      No patients waiting for vitals
                    </li>}
                </ul>
              </Card>
              <Card title="Active Patients — Record Additional Vitals" noPadding data-id="element-3940">
                <ul className="divide-y divide-gray-200" data-id="element-3941">
                  {activeEncounters.length > 0 ? activeEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-3942">
                          <div className="flex items-center" data-id="element-3943">
                            <div className="bg-green-100 p-2 rounded-full mr-4" data-id="element-3944">
                              <Activity className="h-6 w-6 text-green-600" data-id="element-3945" />
                            </div>
                            <div data-id="element-3946">
                              <p className="text-sm font-medium text-blue-600" data-id="element-3947">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-3948">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-3949">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'} •{' '}
                                <span className="text-gray-400" data-id="element-3950">
                                  Stage: {enc.stage}
                                </span>
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-3951" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-3952">
                      No active patients
                    </li>}
                </ul>
              </Card>
            </div> : <>
              <div className="flex justify-between items-center" data-id="element-3953">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-3954">
                  Nursing Assessment
                </h1>
                <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-3955">
                  Back to Queue
                </Button>
              </div>

              <Card className="bg-blue-50 border-blue-200" data-id="element-3956">
                <div className="flex justify-between items-start" data-id="element-3957">
                  <div data-id="element-3958">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-3959">
                      {patient?.firstName || 'Patient'}{' '}
                      {patient?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-3960">
                      BMC: {patient?.bmcFileNumber || '—'} •{' '}
                      {patient?.age ?? '—'} yrs • {patient?.sex || '—'}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-3961">
                    <p className="text-sm font-medium text-red-600" data-id="element-3962">
                      Allergies: {medicalAlerts.allergies || '—'}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3963">
                      HIV Status: {medicalAlerts.hivStatus}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3964">
                <div className="lg:col-span-2" data-id="element-3965">
                  <Card title="Enter Vitals" data-id="element-3966">
                    <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-3967">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-3968">
                        <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                    ...vitals,
                    bp: e.target.value
                  })} data-id="element-3969" />
                        <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                    ...vitals,
                    temp: e.target.value
                  })} data-id="element-3970" />
                        <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                    ...vitals,
                    pulse: e.target.value
                  })} data-id="element-3971" />
                        <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                    ...vitals,
                    resp: e.target.value
                  })} data-id="element-3972" />
                        <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                    ...vitals,
                    sp02: e.target.value
                  })} data-id="element-3973" />
                        <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                    ...vitals,
                    painScore: e.target.value
                  })} data-id="element-3974" />

                        <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-3975" />
                        <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-3976" />
                        <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-3977" />
                      </div>

                      <div className="border-t border-gray-200 pt-4" data-id="element-3978">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3979">
                          AVPU Scale
                        </label>
                        <div className="flex space-x-4" data-id="element-3980">
                          {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-3981">
                                <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                        ...vitals,
                        avpu: e.target.value
                      })} className="mr-2" data-id="element-3982" />
                                {level}
                              </label>)}
                        </div>
                      </div>

                      {/* Medical Alerts (moved from Reception) */}
                      <div className="border-t border-gray-200 pt-4" data-id="element-3983">
                        <h4 className="text-md font-semibold text-gray-900 mb-3" data-id="element-3984">
                          Medical Alerts
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3985">
                          <Input label="Known Allergies" placeholder="e.g. Penicillin, Peanuts (or 'None')" required value={medicalAlerts.allergies} onChange={e => setMedicalAlerts({
                      ...medicalAlerts,
                      allergies: e.target.value
                    })} data-id="element-3986" />
                          <div data-id="element-3987">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3988">
                              HIV Status
                            </label>
                            <select required value={medicalAlerts.hivStatus} onChange={e => setMedicalAlerts({
                        ...medicalAlerts,
                        hivStatus: e.target.value as 'Positive' | 'Negative' | 'Unknown'
                      })} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-3989">
                              <option value="Unknown" data-id="element-3990">Unknown</option>
                              <option value="Positive" data-id="element-3991">Positive</option>
                              <option value="Negative" data-id="element-3992">Negative</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4" data-id="element-3993">
                        <Button type="submit" size="lg" data-id="element-3994">
                          Save & Forward to Clinician
                        </Button>
                      </div>
                    </form>
                  </Card>

                  {/* Stat / Start Dose Section */}
                  <Card title="Stat / Start Dose Administered" className="mt-6" action={<Syringe className="w-5 h-5 text-pink-500" data-id="element-3996" />} data-id="element-3995">
                    <p className="text-xs text-gray-500 mb-4" data-id="element-3997">
                      Record any medication given to the patient as a start/stat
                      dose. These will appear on the patient file and be visible
                      to the clinician.
                    </p>

                    {/* Existing Stat Doses */}
                    {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="mb-4 space-y-2" data-id="element-3998">
                          {selectedEncounter.statDoses.map(d => <div key={d.id} className="flex justify-between items-start bg-pink-50 border border-pink-200 rounded p-3 text-sm" data-id="element-3999">
                              <div data-id="element-4000">
                                <p className="font-bold text-gray-900" data-id="element-4001">
                                  {d.drugName}{' '}
                                  <span className="text-gray-600 font-normal" data-id="element-4002">
                                    {d.dose} • {d.route}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5" data-id="element-4003">
                                  Given by {d.nurseName} •{' '}
                                  {new Date(d.givenAt).toLocaleString()}
                                </p>
                                {d.notes && <p className="text-xs text-gray-700 mt-1 italic" data-id="element-4004">
                                    {d.notes}
                                  </p>}
                              </div>
                              <button onClick={() => handleRemoveStatDose(d.id)} className="text-gray-400 hover:text-red-600 p-1" title="Remove" data-id="element-4005">
                                <Trash2 className="w-4 h-4" data-id="element-4006" />
                              </button>
                            </div>)}
                        </div>}

                    {/* Add New Stat Dose */}
                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4" data-id="element-4007">
                      <Input label="Drug Name" className="col-span-2" placeholder="e.g. Paracetamol" value={statDoseForm.drugName} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  drugName: e.target.value
                })} data-id="element-4008" />
                      <Input label="Dose" placeholder="e.g. 500mg" value={statDoseForm.dose} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  dose: e.target.value
                })} data-id="element-4009" />
                      <div data-id="element-4010">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4011">
                          Route
                        </label>
                        <select value={statDoseForm.route} onChange={e => setStatDoseForm({
                    ...statDoseForm,
                    route: e.target.value
                  })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4012">
                          <option value="PO" data-id="element-4013">Oral (PO)</option>
                          <option value="IV" data-id="element-4014">Intravenous (IV)</option>
                          <option value="IM" data-id="element-4015">Intramuscular (IM)</option>
                          <option value="SC" data-id="element-4016">Subcutaneous (SC)</option>
                          <option value="PR" data-id="element-4017">Per Rectum (PR)</option>
                          <option value="Topical" data-id="element-4018">Topical</option>
                        </select>
                      </div>
                      <Input label="Notes (optional)" className="col-span-2" placeholder="Reason or observation..." value={statDoseForm.notes} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  notes: e.target.value
                })} data-id="element-4019" />
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-4020">
                      <Button type="button" onClick={handleAddStatDose} disabled={!statDoseForm.drugName} className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-gray-300" data-id="element-4021">
                        <Syringe className="w-4 h-4 mr-2" data-id="element-4022" /> Mark Stat Dose
                        Given
                      </Button>
                    </div>
                  </Card>
                </div>

                <div className="lg:col-span-1" data-id="element-4023">
                  <Card title="Vitals History" className="h-full" data-id="element-4024">
                    {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-4025">
                        {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-4026">
                            <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-4027">
                              {new Date(enc.startedAt).toLocaleDateString()}
                            </p>
                            <div className="grid grid-cols-2 gap-2" data-id="element-4028">
                              <div data-id="element-4029">
                                <span className="text-gray-500" data-id="element-4030">BP:</span>{' '}
                                {enc.vitals?.bp}
                              </div>
                              <div data-id="element-4031">
                                <span className="text-gray-500" data-id="element-4032">Temp:</span>{' '}
                                {enc.vitals?.temp}°C
                              </div>
                              <div data-id="element-4033">
                                <span className="text-gray-500" data-id="element-4034">Pulse:</span>{' '}
                                {enc.vitals?.pulse}
                              </div>
                              <div data-id="element-4035">
                                <span className="text-gray-500" data-id="element-4036">SpO2:</span>{' '}
                                {enc.vitals?.sp02}%
                              </div>
                              <div data-id="element-4037">
                                <span className="text-gray-500" data-id="element-4038">Weight:</span>{' '}
                                {enc.vitals?.weight}kg
                              </div>
                              <div data-id="element-4039">
                                <span className="text-gray-500" data-id="element-4040">BMI:</span>{' '}
                                {enc.vitals?.bmi}
                              </div>
                            </div>
                          </div>)}
                      </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-4041">
                        <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-4042" />
                        <p data-id="element-4043">No previous vitals recorded</p>
                      </div>}
                  </Card>
                </div>
              </div>
            </>}
        </div>}

      {activeTab === 'maternity' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4044">
          <div className="lg:col-span-1" data-id="element-4045">
            <Card title="Active Maternity Files" noPadding data-id="element-4046">
              <ul className="divide-y divide-gray-200" data-id="element-4047">
                {activeMaternityFiles.length > 0 ? activeMaternityFiles.map(file => <li key={file.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedMaternityFile?.id === file.id ? 'bg-pink-50' : ''}`} onClick={() => setSelectedMaternityFile(file)} data-id="element-4048">
                      <div className="flex items-center" data-id="element-4049">
                        <div className="bg-pink-100 p-2 rounded-full mr-3" data-id="element-4050">
                          <Baby className="h-5 w-5 text-pink-600" data-id="element-4051" />
                        </div>
                        <div data-id="element-4052">
                          <p className="text-sm font-medium text-blue-600" data-id="element-4053">
                            {file.bmcFileNumber}
                          </p>
                          <p className="text-base font-bold text-gray-900" data-id="element-4054">
                            {file.patient.firstName} {file.patient.surname}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-4055">
                            Registered:{' '}
                            {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-4056">
                    <Baby className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-4057" />
                    <p data-id="element-4058">No active maternity patients</p>
                  </li>}
              </ul>
            </Card>
          </div>

          <div className="lg:col-span-2" data-id="element-4059">
            {selectedMaternityFile ? <MaternityErrorBoundary key={selectedMaternityFile.id} onReset={() => setSelectedMaternityFile(null)} data-id="element-4060">
                <div className="space-y-6" data-id="element-4061">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200 sticky top-0 z-10" data-id="element-4062">
                    <div data-id="element-4063">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-4064">
                        {selectedMaternityFile.patient?.firstName}{' '}
                        {selectedMaternityFile.patient?.surname}
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-4065">
                        File: {selectedMaternityFile.bmcFileNumber} | Age:{' '}
                        {selectedMaternityFile.patient?.age}
                      </p>
                    </div>
                    <div className="flex space-x-2" data-id="element-4066">
                      <Button variant="outline" onClick={handleSaveMaternityDraft} data-id="element-4067">
                        <Save className="w-4 h-4 mr-2" data-id="element-4068" /> Save Draft
                      </Button>
                      <Button variant="primary" className="bg-pink-600 hover:bg-pink-700 text-white" onClick={handleCompleteMaternity} data-id="element-4069">
                        <CheckCircle className="w-4 h-4 mr-2" data-id="element-4070" /> Complete Visit
                      </Button>
                    </div>
                  </div>

                  <Card title="Admission & General Exam" data-id="element-4071">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4072">
                      <Input type="date" label="Date of Admission" value={selectedMaternityFile.admission.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  admission: {
                    ...selectedMaternityFile.admission,
                    date: e.target.value
                  }
                })} data-id="element-4073" />
                      <Input type="time" label="Time of Admission" value={selectedMaternityFile.admission.time} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  admission: {
                    ...selectedMaternityFile.admission,
                    time: e.target.value
                  }
                })} data-id="element-4074" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-3 border-b pb-1" data-id="element-4075">
                      General Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4076">
                      <Input label="Temp" value={selectedMaternityFile.generalExam.temp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    temp: e.target.value
                  }
                })} data-id="element-4077" />
                      <Input label="Pulse" value={selectedMaternityFile.generalExam.pulse} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    pulse: e.target.value
                  }
                })} data-id="element-4078" />
                      <Input label="Respiration" value={selectedMaternityFile.generalExam.respiration} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    respiration: e.target.value
                  }
                })} data-id="element-4079" />
                      <Input label="BP" value={selectedMaternityFile.generalExam.bp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    bp: e.target.value
                  }
                })} data-id="element-4080" />
                      <Input label="Oedema" value={selectedMaternityFile.generalExam.oedema} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    oedema: e.target.value
                  }
                })} data-id="element-4081" />
                      <Input label="Anemia" value={selectedMaternityFile.generalExam.anemia} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    anemia: e.target.value
                  }
                })} data-id="element-4082" />
                      <Input label="Hb%" value={selectedMaternityFile.generalExam.hb} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    hb: e.target.value
                  }
                })} data-id="element-4083" />
                      <Input label="ABO" value={selectedMaternityFile.generalExam.abo} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    abo: e.target.value
                  }
                })} data-id="element-4084" />
                      <Input label="Rh" value={selectedMaternityFile.generalExam.rh} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    rh: e.target.value
                  }
                })} data-id="element-4085" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-4086">
                      <Input label="Cardiovascular System" value={selectedMaternityFile.generalExam.cardiovascularSystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    cardiovascularSystem: e.target.value
                  }
                })} data-id="element-4087" />
                      <Input label="Respiratory System" value={selectedMaternityFile.generalExam.respiratorySystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    respiratorySystem: e.target.value
                  }
                })} data-id="element-4088" />
                      <Input label="Central Nervous System" value={selectedMaternityFile.generalExam.centralNervousSystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    centralNervousSystem: e.target.value
                  }
                })} data-id="element-4089" />
                      <Input label="Breasts" value={selectedMaternityFile.generalExam.breasts} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    breasts: e.target.value
                  }
                })} data-id="element-4090" />
                      <Input label="Varicosities" value={selectedMaternityFile.generalExam.varicosities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    varicosities: e.target.value
                  }
                })} data-id="element-4091" />
                      <Input label="Vaginal Discharge" value={selectedMaternityFile.generalExam.vaginalDischarge} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    vaginalDischarge: e.target.value
                  }
                })} data-id="element-4092" />
                    </div>
                    <div className="mt-4" data-id="element-4093">
                      <Input label="Other Abnormalities" value={selectedMaternityFile.generalExam.otherAbnormalities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    otherAbnormalities: e.target.value
                  }
                })} data-id="element-4094" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4095">
                      MS Urine
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4096">
                      <Input label="SG" value={selectedMaternityFile.generalExam.msUrine.sg} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      sg: e.target.value
                    }
                  }
                })} data-id="element-4097" />
                      <Input label="Albumen" value={selectedMaternityFile.generalExam.msUrine.albumen} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      albumen: e.target.value
                    }
                  }
                })} data-id="element-4098" />
                      <Input label="Acetone" value={selectedMaternityFile.generalExam.msUrine.acetone} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      acetone: e.target.value
                    }
                  }
                })} data-id="element-4099" />
                      <Input label="Sugar" value={selectedMaternityFile.generalExam.msUrine.sugar} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      sugar: e.target.value
                    }
                  }
                })} data-id="element-4100" />
                    </div>
                  </Card>

                  <Card title="Present Pregnancy" data-id="element-4101">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4102">
                      <Input type="date" label="LMP" value={selectedMaternityFile.presentPregnancy.lmp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    lmp: e.target.value
                  }
                })} data-id="element-4103" />
                      <Input label="Quickening" value={selectedMaternityFile.presentPregnancy.quickening} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    quickening: e.target.value
                  }
                })} data-id="element-4104" />
                      <Input type="date" label="EDD" value={selectedMaternityFile.presentPregnancy.edd} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    edd: e.target.value
                  }
                })} data-id="element-4105" />
                      <Input label="Gestation Age (Weeks)" value={selectedMaternityFile.presentPregnancy.gestationAge} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    gestationAge: e.target.value
                  }
                })} data-id="element-4106" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4107">
                      <Input label="Summary of ANC Findings 1st BP" value={selectedMaternityFile.presentPregnancy.ancSummaryFirstBp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    ancSummaryFirstBp: e.target.value
                  }
                })} data-id="element-4108" />
                      <Input label="At Weeks" value={selectedMaternityFile.presentPregnancy.ancSummaryAtWeeks} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    ancSummaryAtWeeks: e.target.value
                  }
                })} data-id="element-4109" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4110">
                      <Input type="date" label="Syphilis Test Date" value={selectedMaternityFile.presentPregnancy.syphilisTest.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      date: e.target.value
                    }
                  }
                })} data-id="element-4111" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.syphilisTest.result} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      result: e.target.value
                    }
                  }
                })} data-id="element-4112" />
                      <Input label="Treatment" value={selectedMaternityFile.presentPregnancy.syphilisTest.treatment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      treatment: e.target.value
                    }
                  }
                })} data-id="element-4113" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4114">
                      <Input type="date" label="HP Test Date" value={selectedMaternityFile.presentPregnancy.hpTest.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    hpTest: {
                      ...selectedMaternityFile.presentPregnancy.hpTest,
                      date: e.target.value
                    }
                  }
                })} data-id="element-4115" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.hpTest.result} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    hpTest: {
                      ...selectedMaternityFile.presentPregnancy.hpTest,
                      result: e.target.value
                    }
                  }
                })} data-id="element-4116" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4117">
                      <Input label="Pelvic Assessment" value={selectedMaternityFile.presentPregnancy.pelvicAssessment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    pelvicAssessment: e.target.value
                  }
                })} data-id="element-4118" />
                      <Input label="Abnormalities" value={selectedMaternityFile.presentPregnancy.abnormalities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    abnormalities: e.target.value
                  }
                })} data-id="element-4119" />
                    </div>
                  </Card>

                  <Card title="Observations for Latent Phase Labour" data-id="element-4120">
                    <div className="overflow-x-auto" data-id="element-4121">
                      <table className="w-full text-sm text-left" data-id="element-4122">
                        <thead className="bg-gray-50" data-id="element-4123">
                          <tr data-id="element-4124">
                            <th className="p-2 border" data-id="element-4125">Date</th>
                            <th className="p-2 border" data-id="element-4126">Time</th>
                            <th className="p-2 border" data-id="element-4127">Pulse</th>
                            <th className="p-2 border" data-id="element-4128">BP</th>
                            <th className="p-2 border" data-id="element-4129">FH</th>
                            <th className="p-2 border" data-id="element-4130">Temp</th>
                            <th className="p-2 border" data-id="element-4131">Urinalysis</th>
                            <th className="p-2 border" data-id="element-4132">Contractions</th>
                            <th className="p-2 border" data-id="element-4133">Cervical Dilation</th>
                            <th className="p-2 border" data-id="element-4134">Membranes Liquor</th>
                            <th className="p-2 border" data-id="element-4135">Remarks</th>
                            <th className="p-2 border" data-id="element-4136">Sign</th>
                            <th className="p-2 border" data-id="element-4137"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4138">
                          {selectedMaternityFile.labourObservations.map((obs, index) => <tr key={obs.id} data-id="element-4139">
                                <td className="p-1 border" data-id="element-4140">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4141" />
                                </td>
                                <td className="p-1 border" data-id="element-4142">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].time = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4143" />
                                </td>
                                <td className="p-1 border" data-id="element-4144">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].pulse = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4145" />
                                </td>
                                <td className="p-1 border" data-id="element-4146">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].bp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4147" />
                                </td>
                                <td className="p-1 border" data-id="element-4148">
                                  <input className="w-full p-1 border rounded" value={obs.fh} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].fh = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4149" />
                                </td>
                                <td className="p-1 border" data-id="element-4150">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].temp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4151" />
                                </td>
                                <td className="p-1 border" data-id="element-4152">
                                  <input className="w-full p-1 border rounded" value={obs.urinalysis} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].urinalysis = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4153" />
                                </td>
                                <td className="p-1 border" data-id="element-4154">
                                  <input className="w-full p-1 border rounded" value={obs.contractions} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].contractions = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4155" />
                                </td>
                                <td className="p-1 border" data-id="element-4156">
                                  <input className="w-full p-1 border rounded" value={obs.cervicalDilation} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].cervicalDilation = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4157" />
                                </td>
                                <td className="p-1 border" data-id="element-4158">
                                  <input className="w-full p-1 border rounded" value={obs.membranesLiquor} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].membranesLiquor = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4159" />
                                </td>
                                <td className="p-1 border" data-id="element-4160">
                                  <input className="w-full p-1 border rounded" value={obs.remarks} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].remarks = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4161" />
                                </td>
                                <td className="p-1 border" data-id="element-4162">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].sign = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4163" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4164">
                                  <button onClick={() => {
                          const newObs = selectedMaternityFile.labourObservations.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4165">
                                    <Trash2 className="w-4 h-4" data-id="element-4166" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                labourObservations: [...selectedMaternityFile.labourObservations, {
                  id: Date.now().toString(),
                  date: '',
                  time: '',
                  pulse: '',
                  bp: '',
                  fh: '',
                  temp: '',
                  urinalysis: '',
                  contractions: '',
                  cervicalDilation: '',
                  membranesLiquor: '',
                  remarks: '',
                  sign: ''
                }]
              })} data-id="element-4167">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Description of Labour and Delivery" data-id="element-4168">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4169">
                      <Input label="Laceration" value={selectedMaternityFile.delivery.laceration} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    laceration: e.target.value
                  }
                })} data-id="element-4170" />
                      <Input label="Anterior" value={selectedMaternityFile.delivery.anterior} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    anterior: e.target.value
                  }
                })} data-id="element-4171" />
                      <Input label="Posterior" value={selectedMaternityFile.delivery.posterior} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    posterior: e.target.value
                  }
                })} data-id="element-4172" />
                      <Input label="None" value={selectedMaternityFile.delivery.none} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    none: e.target.value
                  }
                })} data-id="element-4173" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.repaired} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    repaired: e.target.value
                  }
                })} data-id="element-4174" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4175">
                      <Input label="Membranes" value={selectedMaternityFile.delivery.membranes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranes: e.target.value
                  }
                })} data-id="element-4176" />
                      <Input type="date" label="Date" value={selectedMaternityFile.delivery.membranesDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranesDate: e.target.value
                  }
                })} data-id="element-4177" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.membranesTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranesTime: e.target.value
                  }
                })} data-id="element-4178" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4179">
                      <Input label="Episiotomy" value={selectedMaternityFile.delivery.episiotomy} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    episiotomy: e.target.value
                  }
                })} data-id="element-4180" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.episiotomyRepaired} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    episiotomyRepaired: e.target.value
                  }
                })} data-id="element-4181" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4182">
                      <Input label="Maternal ARV" value={selectedMaternityFile.delivery.maternalARV} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    maternalARV: e.target.value
                  }
                })} data-id="element-4183" />
                      <Input label="Which ones (NPV, AZT, Other)" value={selectedMaternityFile.delivery.arvTypes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    arvTypes: e.target.value
                  }
                })} data-id="element-4184" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4185">
                      <Input label="Uterine Massage" value={selectedMaternityFile.delivery.uterineMassage} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    uterineMassage: e.target.value
                  }
                })} data-id="element-4186" />
                      <Input label="Mother Already on HAART" value={selectedMaternityFile.delivery.motherOnHAART} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    motherOnHAART: e.target.value
                  }
                })} data-id="element-4187" />
                      <Input label="Cytotec" value={selectedMaternityFile.delivery.cytotec} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    cytotec: e.target.value
                  }
                })} data-id="element-4188" />
                      <Input label="Syntometrine" value={selectedMaternityFile.delivery.syntometrine} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    syntometrine: e.target.value
                  }
                })} data-id="element-4189" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4190">
                      <Input type="date" label="1st Stage Began Date" value={selectedMaternityFile.delivery.firstStageBeganDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    firstStageBeganDate: e.target.value
                  }
                })} data-id="element-4191" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.firstStageBeganTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    firstStageBeganTime: e.target.value
                  }
                })} data-id="element-4192" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4193">
                      <Input label="Placenta Delivery" value={selectedMaternityFile.delivery.placentaDelivery} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDelivery: e.target.value
                  }
                })} data-id="element-4194" />
                      <Input type="date" label="Delivered Date" value={selectedMaternityFile.delivery.placentaDeliveredDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDeliveredDate: e.target.value
                  }
                })} data-id="element-4195" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.placentaDeliveredTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDeliveredTime: e.target.value
                  }
                })} data-id="element-4196" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4197">
                      <Input type="date" label="Complete Dilation Date" value={selectedMaternityFile.delivery.completeDilationDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    completeDilationDate: e.target.value
                  }
                })} data-id="element-4198" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.completeDilationTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    completeDilationTime: e.target.value
                  }
                })} data-id="element-4199" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4200">
                      <Input label="Abnormality at Dilation" value={selectedMaternityFile.delivery.abnormalityAtDilation} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDilation: e.target.value
                  }
                })} data-id="element-4201" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDilationDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDilationDesc: e.target.value
                  }
                })} data-id="element-4202" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4203">
                      <Input label="Postpartum Hemorrhage" value={selectedMaternityFile.delivery.postpartumHemorrhage} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    postpartumHemorrhage: e.target.value
                  }
                })} data-id="element-4204" />
                      <Input label="Delivery Method" value={selectedMaternityFile.delivery.deliveryMethod} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    deliveryMethod: e.target.value
                  }
                })} data-id="element-4205" />
                      <Input label="EBL (cc)" value={selectedMaternityFile.delivery.ebl} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    ebl: e.target.value
                  }
                })} data-id="element-4206" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4207">
                      <Input label="Abnormality at Delivery" value={selectedMaternityFile.delivery.abnormalityAtDelivery} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDelivery: e.target.value
                  }
                })} data-id="element-4208" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDeliveryDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDeliveryDesc: e.target.value
                  }
                })} data-id="element-4209" />
                      <Input label="Complications" value={selectedMaternityFile.delivery.complications} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    complications: e.target.value
                  }
                })} data-id="element-4210" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4211">
                      Delivery Summary
                    </h4>
                    <div className="overflow-x-auto" data-id="element-4212">
                      <table className="w-full text-sm text-left" data-id="element-4213">
                        <thead className="bg-gray-50" data-id="element-4214">
                          <tr data-id="element-4215">
                            <th className="p-2 border" data-id="element-4216">Date</th>
                            <th className="p-2 border" data-id="element-4217">Time of Birth</th>
                            <th className="p-2 border" data-id="element-4218">Sex</th>
                            <th className="p-2 border" data-id="element-4219">Weight</th>
                            <th className="p-2 border" data-id="element-4220">Delivered By</th>
                            <th className="p-2 border" data-id="element-4221"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4222">
                          {selectedMaternityFile.delivery.deliverySummary.map((sum, index) => <tr key={sum.id} data-id="element-4223">
                                <td className="p-1 border" data-id="element-4224">
                                  <Input type="date" value={sum.date} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4225" />
                                </td>
                                <td className="p-1 border" data-id="element-4226">
                                  <Input type="time" value={sum.timeOfBirth} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].timeOfBirth = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4227" />
                                </td>
                                <td className="p-1 border" data-id="element-4228">
                                  <Input value={sum.sex} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].sex = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4229" />
                                </td>
                                <td className="p-1 border" data-id="element-4230">
                                  <Input value={sum.weight} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].weight = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4231" />
                                </td>
                                <td className="p-1 border" data-id="element-4232">
                                  <Input value={sum.deliveredBy} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].deliveredBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4233" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4234">
                                  <button onClick={() => {
                          const newSum = selectedMaternityFile.delivery.deliverySummary.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4235">
                                    <Trash2 className="w-4 h-4" data-id="element-4236" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                delivery: {
                  ...selectedMaternityFile.delivery,
                  deliverySummary: [...selectedMaternityFile.delivery.deliverySummary, {
                    id: Date.now().toString(),
                    date: '',
                    timeOfBirth: '',
                    sex: '',
                    weight: '',
                    deliveredBy: ''
                  }]
                }
              })} data-id="element-4237">
                      + Add Baby
                    </Button>
                  </Card>

                  <Card title="Newborn Evaluation" data-id="element-4238">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4239">
                      <Input label="EGA" value={selectedMaternityFile.newbornEvaluation.ega} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    ega: e.target.value
                  }
                })} data-id="element-4240" />
                      <Input label="Alive" value={selectedMaternityFile.newbornEvaluation.alive} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    alive: e.target.value
                  }
                })} data-id="element-4241" />
                      <Input label="Neonatal Death" value={selectedMaternityFile.newbornEvaluation.neonatalDeath} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    neonatalDeath: e.target.value
                  }
                })} data-id="element-4242" />
                      <Input label="Cause of Death" value={selectedMaternityFile.newbornEvaluation.causeOfDeath} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    causeOfDeath: e.target.value
                  }
                })} data-id="element-4243" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4244">
                      <Input label="Apgar 1 Min" value={selectedMaternityFile.newbornEvaluation.apgar1} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar1: e.target.value
                  }
                })} data-id="element-4245" />
                      <Input label="Apgar 5 Min" value={selectedMaternityFile.newbornEvaluation.apgar5} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar5: e.target.value
                  }
                })} data-id="element-4246" />
                      <Input label="Apgar 10 Min" value={selectedMaternityFile.newbornEvaluation.apgar10} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar10: e.target.value
                  }
                })} data-id="element-4247" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4248">
                      <Input label="General Exam" value={selectedMaternityFile.newbornEvaluation.generalExam} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    generalExam: e.target.value
                  }
                })} data-id="element-4249" />
                      <Input label="Congenital Malformation" value={selectedMaternityFile.newbornEvaluation.congenitalMalformation} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    congenitalMalformation: e.target.value
                  }
                })} data-id="element-4250" />
                      <Input label="Describe" value={selectedMaternityFile.newbornEvaluation.congenitalMalformationDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    congenitalMalformationDesc: e.target.value
                  }
                })} data-id="element-4251" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4252">
                      <Input label="Infant ARVs" value={selectedMaternityFile.newbornEvaluation.infantARVs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    infantARVs: e.target.value
                  }
                })} data-id="element-4253" />
                      <Input label="Infant Feeding" value={selectedMaternityFile.newbornEvaluation.infantFeeding} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    infantFeeding: e.target.value
                  }
                })} data-id="element-4254" />
                      <Input label="Resuscitation Methods" value={selectedMaternityFile.newbornEvaluation.resuscitationMethods} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    resuscitationMethods: e.target.value
                  }
                })} data-id="element-4255" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4256">
                      <Input label="Crown Heel Length" value={selectedMaternityFile.newbornEvaluation.crownHeelLength} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    crownHeelLength: e.target.value
                  }
                })} data-id="element-4257" />
                      <Input label="Head Circumference" value={selectedMaternityFile.newbornEvaluation.headCircumference} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    headCircumference: e.target.value
                  }
                })} data-id="element-4258" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4259">
                      Physical Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4260">
                      <Input label="Skin Jaundice" value={selectedMaternityFile.newbornEvaluation.skinJaundice} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    skinJaundice: e.target.value
                  }
                })} data-id="element-4261" />
                      <Input label="Skin Other" value={selectedMaternityFile.newbornEvaluation.skinOther} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    skinOther: e.target.value
                  }
                })} data-id="element-4262" />
                      <Input label="Fontanelles" value={selectedMaternityFile.newbornEvaluation.fontanelles} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    fontanelles: e.target.value
                  }
                })} data-id="element-4263" />
                      <Input label="Eyes" value={selectedMaternityFile.newbornEvaluation.eyes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    eyes: e.target.value
                  }
                })} data-id="element-4264" />
                      <Input label="Eyes Treatment" value={selectedMaternityFile.newbornEvaluation.eyesTreatment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    eyesTreatment: e.target.value
                  }
                })} data-id="element-4265" />
                      <Input label="Mouth" value={selectedMaternityFile.newbornEvaluation.mouth} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    mouth: e.target.value
                  }
                })} data-id="element-4266" />
                      <Input label="Neck" value={selectedMaternityFile.newbornEvaluation.neck} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    neck: e.target.value
                  }
                })} data-id="element-4267" />
                      <Input label="Chest" value={selectedMaternityFile.newbornEvaluation.chest} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    chest: e.target.value
                  }
                })} data-id="element-4268" />
                      <Input label="Abdomen" value={selectedMaternityFile.newbornEvaluation.abdomen} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    abdomen: e.target.value
                  }
                })} data-id="element-4269" />
                      <Input label="Umbilicus" value={selectedMaternityFile.newbornEvaluation.umbilicus} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    umbilicus: e.target.value
                  }
                })} data-id="element-4270" />
                      <Input label="Genitalia" value={selectedMaternityFile.newbornEvaluation.genitalia} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    genitalia: e.target.value
                  }
                })} data-id="element-4271" />
                      <Input label="Urine" value={selectedMaternityFile.newbornEvaluation.urine} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    urine: e.target.value
                  }
                })} data-id="element-4272" />
                      <Input label="Anus" value={selectedMaternityFile.newbornEvaluation.anus} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    anus: e.target.value
                  }
                })} data-id="element-4273" />
                      <Input label="Meconium" value={selectedMaternityFile.newbornEvaluation.meconium} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    meconium: e.target.value
                  }
                })} data-id="element-4274" />
                      <Input label="Upper Limbs" value={selectedMaternityFile.newbornEvaluation.upperLimbs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    upperLimbs: e.target.value
                  }
                })} data-id="element-4275" />
                      <Input label="Erb Paralysis" value={selectedMaternityFile.newbornEvaluation.erbParalysis} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    erbParalysis: e.target.value
                  }
                })} data-id="element-4276" />
                      <Input label="Lower Limbs" value={selectedMaternityFile.newbornEvaluation.lowerLimbs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    lowerLimbs: e.target.value
                  }
                })} data-id="element-4277" />
                      <Input label="Ortolani Sign" value={selectedMaternityFile.newbornEvaluation.ortolaniSign} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    ortolaniSign: e.target.value
                  }
                })} data-id="element-4278" />
                      <Input label="Back" value={selectedMaternityFile.newbornEvaluation.back} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    back: e.target.value
                  }
                })} data-id="element-4279" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4280">
                      Reflexes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4281">
                      <Input label="Grasp" value={selectedMaternityFile.newbornEvaluation.reflexesGrasp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesGrasp: e.target.value
                  }
                })} data-id="element-4282" />
                      <Input label="Moro (Startle)" value={selectedMaternityFile.newbornEvaluation.reflexesMoro} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesMoro: e.target.value
                  }
                })} data-id="element-4283" />
                      <Input label="Sucking" value={selectedMaternityFile.newbornEvaluation.reflexesSucking} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesSucking: e.target.value
                  }
                })} data-id="element-4284" />
                    </div>
                    <div className="mt-4" data-id="element-4285">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4286">
                        Newborn Progress Notes
                      </label>
                      <textarea className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-pink-500 focus:outline-none" rows={4} value={selectedMaternityFile.newbornEvaluation.progressNotes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    progressNotes: e.target.value
                  }
                })} data-id="element-4287" />
                    </div>
                  </Card>

                  <Card title="Post Delivery Observation Chart" data-id="element-4288">
                    <div className="overflow-x-auto" data-id="element-4289">
                      <table className="w-full text-sm text-left" data-id="element-4290">
                        <thead className="bg-gray-50" data-id="element-4291">
                          <tr data-id="element-4292">
                            <th className="p-2 border" colSpan={2} data-id="element-4293">
                              Time
                            </th>
                            <th className="p-2 border text-center" colSpan={8} data-id="element-4294">
                              Mother
                            </th>
                            <th className="p-2 border text-center" colSpan={7} data-id="element-4295">
                              Baby
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-4296">
                              Sign
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-4297"></th>
                          </tr>
                          <tr data-id="element-4298">
                            <th className="p-2 border" data-id="element-4299">Date</th>
                            <th className="p-2 border" data-id="element-4300">Time</th>
                            <th className="p-2 border" data-id="element-4301">BP</th>
                            <th className="p-2 border" data-id="element-4302">Pulse</th>
                            <th className="p-2 border" data-id="element-4303">Resps</th>
                            <th className="p-2 border" data-id="element-4304">Temp</th>
                            <th className="p-2 border" data-id="element-4305">PV Bleed</th>
                            <th className="p-2 border" data-id="element-4306">Uterus</th>
                            <th className="p-2 border" data-id="element-4307">Bladder</th>
                            <th className="p-2 border" data-id="element-4308">Pallor</th>
                            <th className="p-2 border" data-id="element-4309">Temp</th>
                            <th className="p-2 border" data-id="element-4310">Resps</th>
                            <th className="p-2 border" data-id="element-4311">Cord</th>
                            <th className="p-2 border" data-id="element-4312">Skin</th>
                            <th className="p-2 border" data-id="element-4313">Feed BF/AF</th>
                            <th className="p-2 border" data-id="element-4314">Bowel</th>
                            <th className="p-2 border" data-id="element-4315">Urine</th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4316">
                          {selectedMaternityFile.postDeliveryObservations.map((obs, index) => <tr key={obs.id} data-id="element-4317">
                                <td className="p-1 border" data-id="element-4318">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4319" />
                                </td>
                                <td className="p-1 border" data-id="element-4320">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].time = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4321" />
                                </td>
                                <td className="p-1 border" data-id="element-4322">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].bp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4323" />
                                </td>
                                <td className="p-1 border" data-id="element-4324">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pulse = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4325" />
                                </td>
                                <td className="p-1 border" data-id="element-4326">
                                  <input className="w-full p-1 border rounded" value={obs.resps} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].resps = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4327" />
                                </td>
                                <td className="p-1 border" data-id="element-4328">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].temp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4329" />
                                </td>
                                <td className="p-1 border" data-id="element-4330">
                                  <input className="w-full p-1 border rounded" value={obs.pvBleeding} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pvBleeding = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4331" />
                                </td>
                                <td className="p-1 border" data-id="element-4332">
                                  <input className="w-full p-1 border rounded" value={obs.uterus} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].uterus = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4333" />
                                </td>
                                <td className="p-1 border" data-id="element-4334">
                                  <input className="w-full p-1 border rounded" value={obs.bladder} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].bladder = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4335" />
                                </td>
                                <td className="p-1 border" data-id="element-4336">
                                  <input className="w-full p-1 border rounded" value={obs.pallor} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pallor = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4337" />
                                </td>
                                <td className="p-1 border" data-id="element-4338">
                                  <input className="w-full p-1 border rounded" value={obs.babyTemp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].babyTemp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4339" />
                                </td>
                                <td className="p-1 border" data-id="element-4340">
                                  <input className="w-full p-1 border rounded" value={obs.babyResps} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].babyResps = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4341" />
                                </td>
                                <td className="p-1 border" data-id="element-4342">
                                  <input className="w-full p-1 border rounded" value={obs.cord} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].cord = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4343" />
                                </td>
                                <td className="p-1 border" data-id="element-4344">
                                  <input className="w-full p-1 border rounded" value={obs.skinColour} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].skinColour = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4345" />
                                </td>
                                <td className="p-1 border" data-id="element-4346">
                                  <input className="w-full p-1 border rounded" value={obs.feedingBF} placeholder="BF" onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].feedingBF = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4347" />
                                  <input className="w-full p-1 border rounded mt-1" value={obs.feedingAF} placeholder="AF" onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].feedingAF = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4348" />
                                </td>
                                <td className="p-1 border" data-id="element-4349">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationBowel} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].eliminationBowel = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4350" />
                                </td>
                                <td className="p-1 border" data-id="element-4351">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationUrine} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].eliminationUrine = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4352" />
                                </td>
                                <td className="p-1 border" data-id="element-4353">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].sign = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4354" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4355">
                                  <button onClick={() => {
                          const newObs = selectedMaternityFile.postDeliveryObservations.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4356">
                                    <Trash2 className="w-4 h-4" data-id="element-4357" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                postDeliveryObservations: [...selectedMaternityFile.postDeliveryObservations, {
                  id: Date.now().toString(),
                  date: '',
                  time: '',
                  bp: '',
                  pulse: '',
                  resps: '',
                  temp: '',
                  pvBleeding: '',
                  uterus: '',
                  bladder: '',
                  pallor: '',
                  babyTemp: '',
                  babyResps: '',
                  cord: '',
                  skinColour: '',
                  feedingBF: '',
                  feedingAF: '',
                  eliminationBowel: '',
                  eliminationUrine: '',
                  sign: ''
                }]
              })} data-id="element-4358">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Drug Chart" data-id="element-4359">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-4360">
                      <Input label="Ward" value={selectedMaternityFile?.drugChart?.ward || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    ward: e.target.value
                  }
                })} data-id="element-4361" />
                      <Input label="Patient No." value={selectedMaternityFile?.drugChart?.patientNo || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    patientNo: e.target.value
                  }
                })} data-id="element-4362" />
                    </div>
                    <div className="overflow-x-auto" data-id="element-4363">
                      <table className="w-full text-xs text-center border-collapse border border-gray-300 min-w-[1200px]" data-id="element-4364">
                        <thead className="bg-gray-50" data-id="element-4365">
                          <tr data-id="element-4366">
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4367">
                              Date
                            </th>
                            <th className="p-1 border border-gray-300 w-32" rowSpan={3} data-id="element-4368">
                              Drug
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4369">
                              Dosage
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4370">
                              Route of Admin
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4371">
                              Ordered By
                            </th>
                            <th className="p-1 border border-gray-300" colSpan={21} data-id="element-4372">
                              SUPPLIED BY (DATE)
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4373">
                              Supplied By
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4374">
                              Quantity
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4375">
                              Date
                            </th>
                          </tr>
                          <tr data-id="element-4376">
                            {[0, 1, 2].map(colIdx => <th key={colIdx} className="p-1 border border-gray-300" colSpan={7} data-id="element-4377">
                                <input type="date" className="w-full border-gray-300 rounded-sm text-xs" value={selectedMaternityFile?.drugChart?.supplyDates?.[colIdx] || ''} onChange={e => {
                          const newDates = [...(selectedMaternityFile?.drugChart?.supplyDates || ['', '', ''])] as [string, string, string];
                          newDates[colIdx] = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...(selectedMaternityFile!.drugChart || {
                                ward: '',
                                patientNo: '',
                                supplyDates: ['', '', ''],
                                rows: [],
                                signedBy: '',
                                signedDate: ''
                              }),
                              supplyDates: newDates
                            }
                          });
                        }} data-id="element-4378" />
                              </th>)}
                          </tr>
                          <tr data-id="element-4379">
                            {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-4380">
                                {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="p-1 border border-gray-300 w-8 font-normal" data-id="element-4381">
                                      {time}
                                    </th>)}
                              </Fragment>)}
                          </tr>
                        </thead>
                        <tbody data-id="element-4382">
                          {(selectedMaternityFile?.drugChart?.rows || []).map((row, rowIndex) => <tr key={row.id} data-id="element-4383">
                                <td className="p-0 border border-gray-300" data-id="element-4384">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.date} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4385" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4386">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.drug} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].drug = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4387" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4388">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.dosage} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].dosage = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4389" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4390">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.routeOfAdmin} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].routeOfAdmin = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4391" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4392">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.orderedBy} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].orderedBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4393" />
                                </td>
                                {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-4394">
                                    {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                          const key = `${colIdx}-${time}`;
                          return <td key={key} className="p-0 border border-gray-300" data-id="element-4395">
                                          <input type="text" className="w-full border-0 text-xs p-1 text-center" value={row.administration?.[key] || ''} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].administration = {
                                ...newRows[rowIndex].administration,
                                [key]: e.target.value
                              };
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4396" />
                                        </td>;
                        })}
                                  </Fragment>)}
                                <td className="p-0 border border-gray-300" data-id="element-4397">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.suppliedBy} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].suppliedBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4398" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4399">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.quantity} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].quantity = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4400" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4401">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.suppliedDate} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].suppliedDate = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4402" />
                                </td>
                                <td className="p-1 border border-gray-300" data-id="element-4403">
                                  <button type="button" onClick={() => {
                          const newRows = selectedMaternityFile!.drugChart!.rows.filter((_, i) => i !== rowIndex);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4404">
                                    <Trash2 className="w-4 h-4" data-id="element-4405" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => {
                const currentChart = selectedMaternityFile?.drugChart || {
                  ward: '',
                  patientNo: '',
                  supplyDates: ['', '', ''],
                  rows: [],
                  signedBy: '',
                  signedDate: ''
                };
                setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...currentChart,
                    rows: [...currentChart.rows, {
                      id: `drug-${Date.now()}`,
                      date: '',
                      drug: '',
                      dosage: '',
                      routeOfAdmin: '',
                      orderedBy: '',
                      administration: {},
                      suppliedBy: '',
                      quantity: '',
                      suppliedDate: ''
                    }]
                  }
                });
              }} data-id="element-4406">
                      + Add Drug Row
                    </Button>
                    <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-4407">
                      <Input label="Date" type="date" value={selectedMaternityFile?.drugChart?.signedDate || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    signedDate: e.target.value
                  }
                })} data-id="element-4408" />
                      <Input label="Signature" value={selectedMaternityFile?.drugChart?.signedBy || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    signedBy: e.target.value
                  }
                })} data-id="element-4409" />
                    </div>
                  </Card>

                  <Card title="Puerperium Notes" data-id="element-4410">
                    <div className="overflow-x-auto" data-id="element-4411">
                      <table className="w-full text-sm text-left" data-id="element-4412">
                        <thead className="bg-gray-50" data-id="element-4413">
                          <tr data-id="element-4414">
                            <th className="p-2 border w-32" data-id="element-4415">Date</th>
                            <th className="p-2 border" data-id="element-4416">Notes</th>
                            <th className="p-2 border w-48" data-id="element-4417">Signature</th>
                            <th className="p-2 border w-12" data-id="element-4418"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4419">
                          {selectedMaternityFile.puerperiumNotes.map((note, index) => <tr key={note.id} data-id="element-4420">
                                <td className="p-1 border" data-id="element-4421">
                                  <Input type="date" value={note.date} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-4422" />
                                </td>
                                <td className="p-1 border" data-id="element-4423">
                                  <textarea className="w-full p-1 border rounded" rows={2} value={note.notes} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].notes = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-4424" />
                                </td>
                                <td className="p-1 border" data-id="element-4425">
                                  <Input value={note.signature} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].signature = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-4426" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4427">
                                  <button onClick={() => {
                          const newNotes = selectedMaternityFile.puerperiumNotes.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4428">
                                    <Trash2 className="w-4 h-4" data-id="element-4429" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                puerperiumNotes: [...selectedMaternityFile.puerperiumNotes, {
                  id: Date.now().toString(),
                  date: '',
                  notes: '',
                  signature: ''
                }]
              })} data-id="element-4430">
                      + Add Note
                    </Button>
                  </Card>

                  <Card title="Discharge Slip" data-id="element-4431">
                    {(() => {
                const defaultSlip = {
                  smNo: '',
                  gravida: '',
                  patientName: '',
                  party: '',
                  timeOfDischarge: '',
                  age: '',
                  motherDob: '',
                  modeOfDelivery: '',
                  perineum: '',
                  comments: '',
                  pca: '',
                  infantTob: '',
                  apgarScore: '',
                  infantSex: '',
                  dateOfDischarge: '',
                  mother: {
                    bp: '',
                    pallor: '',
                    breast: '',
                    breastfeeding: '',
                    uterus: '',
                    perineum: '',
                    lochia: '',
                    bowels: '',
                    urine: '',
                    familyPlanning: '',
                    dangerSigns: '',
                    treatment: '',
                    vitaminA: ''
                  },
                  infant: {
                    birthWeight: '',
                    dischargeWeight: '',
                    pallor: '',
                    jaundice: '',
                    passedMeconiumUrine: '',
                    feeding: '',
                    feedingOption: '',
                    breastFeeding: '',
                    bcg: '',
                    opv: '',
                    birthRecord: '',
                    gc: '',
                    treatment: ''
                  },
                  sixDaysPostnatalDate: '',
                  sixWeeksPostnatalDate: '',
                  nameInFull: ''
                };
                const slip = selectedMaternityFile.dischargeSlip || defaultSlip;
                const updateSlip = (patch: Partial<typeof defaultSlip>) => {
                  setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    dischargeSlip: {
                      ...slip,
                      ...patch
                    }
                  });
                };
                const updateMother = (patch: Partial<typeof defaultSlip.mother>) => {
                  updateSlip({
                    mother: {
                      ...slip.mother,
                      ...patch
                    }
                  });
                };
                const updateInfant = (patch: Partial<typeof defaultSlip.infant>) => {
                  updateSlip({
                    infant: {
                      ...slip.infant,
                      ...patch
                    }
                  });
                };
                return <div className="space-y-6" data-id="element-4432">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4433">
                            <Input label="SM No" value={slip.smNo} onChange={e => updateSlip({
                      smNo: e.target.value
                    })} data-id="element-4434" />
                            <Input label="Gravida" value={slip.gravida} onChange={e => updateSlip({
                      gravida: e.target.value
                    })} data-id="element-4435" />
                            <Input label="Patient's Name" value={slip.patientName} onChange={e => updateSlip({
                      patientName: e.target.value
                    })} data-id="element-4436" />
                            <Input label="Party" value={slip.party} onChange={e => updateSlip({
                      party: e.target.value
                    })} data-id="element-4437" />
                            <Input label="Time of Discharge" type="time" value={slip.timeOfDischarge} onChange={e => updateSlip({
                      timeOfDischarge: e.target.value
                    })} data-id="element-4438" />
                            <Input label="Age" value={slip.age} onChange={e => updateSlip({
                      age: e.target.value
                    })} data-id="element-4439" />
                          </div>

                          <div data-id="element-4440">
                            <h4 className="font-semibold text-sm uppercase mb-2 border-b pb-1" data-id="element-4441">
                              Labour and Delivery Summary
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4442">
                              <div data-id="element-4443">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-4444">
                                  Mother
                                </h5>
                                <div className="space-y-3" data-id="element-4445">
                                  <Input label="D.O.B" type="date" value={slip.motherDob} onChange={e => updateSlip({
                            motherDob: e.target.value
                          })} data-id="element-4446" />
                                  <Input label="M.O.D (Mode of Delivery)" value={slip.modeOfDelivery} onChange={e => updateSlip({
                            modeOfDelivery: e.target.value
                          })} data-id="element-4447" />
                                  <Input label="Perineum" value={slip.perineum} onChange={e => updateSlip({
                            perineum: e.target.value
                          })} data-id="element-4448" />
                                  <div data-id="element-4449">
                                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4450">
                                      Comments
                                    </label>
                                    <textarea className="w-full p-2 border rounded text-sm" rows={2} value={slip.comments} onChange={e => updateSlip({
                              comments: e.target.value
                            })} data-id="element-4451" />
                                  </div>
                                  <Input label="P.C.A" value={slip.pca} onChange={e => updateSlip({
                            pca: e.target.value
                          })} data-id="element-4452" />
                                </div>
                              </div>
                              <div data-id="element-4453">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-4454">
                                  Infant
                                </h5>
                                <div className="space-y-3" data-id="element-4455">
                                  <Input label="T.O.B (Time of Birth)" value={slip.infantTob} onChange={e => updateSlip({
                            infantTob: e.target.value
                          })} data-id="element-4456" />
                                  <Input label="A/S (Apgar Score)" value={slip.apgarScore} onChange={e => updateSlip({
                            apgarScore: e.target.value
                          })} data-id="element-4457" />
                                  <Input label="Sex" value={slip.infantSex} onChange={e => updateSlip({
                            infantSex: e.target.value
                          })} data-id="element-4458" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div data-id="element-4459">
                            <div className="flex justify-between items-end border-b pb-1 mb-2" data-id="element-4460">
                              <h4 className="font-semibold text-sm uppercase" data-id="element-4461">
                                Discharge Summary
                              </h4>
                              <div className="w-64" data-id="element-4462">
                                <Input label="Date of Discharge" type="date" value={slip.dateOfDischarge} onChange={e => updateSlip({
                          dateOfDischarge: e.target.value
                        })} data-id="element-4463" />
                              </div>
                            </div>

                            <div className="bg-pink-50 p-3 rounded mb-4" data-id="element-4464">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4465">
                                Mother
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4466">
                                <Input label="BP" value={slip.mother.bp} onChange={e => updateMother({
                          bp: e.target.value
                        })} data-id="element-4467" />
                                <Input label="Pallor" value={slip.mother.pallor} onChange={e => updateMother({
                          pallor: e.target.value
                        })} data-id="element-4468" />
                                <Input label="Breast" value={slip.mother.breast} onChange={e => updateMother({
                          breast: e.target.value
                        })} data-id="element-4469" />
                                <div data-id="element-4470">
                                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4471">
                                    Breastfeeding
                                  </label>
                                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={slip.mother.breastfeeding} onChange={e => updateMother({
                            breastfeeding: e.target.value
                          })} data-id="element-4472">
                                    <option value="" data-id="element-4473">—</option>
                                    <option value="Yes" data-id="element-4474">Yes</option>
                                    <option value="No" data-id="element-4475">No</option>
                                  </select>
                                </div>
                                <Input label="Uterus" value={slip.mother.uterus} onChange={e => updateMother({
                          uterus: e.target.value
                        })} data-id="element-4476" />
                                <Input label="Perineum" value={slip.mother.perineum} onChange={e => updateMother({
                          perineum: e.target.value
                        })} data-id="element-4477" />
                                <Input label="Lochia" value={slip.mother.lochia} onChange={e => updateMother({
                          lochia: e.target.value
                        })} data-id="element-4478" />
                                <Input label="Bowels" value={slip.mother.bowels} onChange={e => updateMother({
                          bowels: e.target.value
                        })} data-id="element-4479" />
                                <Input label="Urine" value={slip.mother.urine} onChange={e => updateMother({
                          urine: e.target.value
                        })} data-id="element-4480" />
                                <Input label="Family Planning" value={slip.mother.familyPlanning} onChange={e => updateMother({
                          familyPlanning: e.target.value
                        })} data-id="element-4481" />
                                <div className="md:col-span-2" data-id="element-4482">
                                  <Input label="Danger Signs" value={slip.mother.dangerSigns} onChange={e => updateMother({
                            dangerSigns: e.target.value
                          })} data-id="element-4483" />
                                </div>
                                <div className="md:col-span-2" data-id="element-4484">
                                  <Input label="Treatment" value={slip.mother.treatment} onChange={e => updateMother({
                            treatment: e.target.value
                          })} data-id="element-4485" />
                                </div>
                                <Input label="Vitamin A" value={slip.mother.vitaminA} onChange={e => updateMother({
                          vitaminA: e.target.value
                        })} data-id="element-4486" />
                              </div>
                            </div>

                            <div className="bg-blue-50 p-3 rounded mb-4" data-id="element-4487">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4488">
                                Infant
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4489">
                                <Input label="BWT (Birth Weight)" value={slip.infant.birthWeight} onChange={e => updateInfant({
                          birthWeight: e.target.value
                        })} data-id="element-4490" />
                                <Input label="Discharge Weight" value={slip.infant.dischargeWeight} onChange={e => updateInfant({
                          dischargeWeight: e.target.value
                        })} data-id="element-4491" />
                                <Input label="Pallor" value={slip.infant.pallor} onChange={e => updateInfant({
                          pallor: e.target.value
                        })} data-id="element-4492" />
                                <Input label="Jaundice" value={slip.infant.jaundice} onChange={e => updateInfant({
                          jaundice: e.target.value
                        })} data-id="element-4493" />
                                <div className="md:col-span-2" data-id="element-4494">
                                  <Input label="Passed Meconium / Urine" value={slip.infant.passedMeconiumUrine} onChange={e => updateInfant({
                            passedMeconiumUrine: e.target.value
                          })} data-id="element-4495" />
                                </div>
                                <Input label="Feeding" value={slip.infant.feeding} onChange={e => updateInfant({
                          feeding: e.target.value
                        })} data-id="element-4496" />
                                <Input label="Feeding Option" value={slip.infant.feedingOption} onChange={e => updateInfant({
                          feedingOption: e.target.value
                        })} data-id="element-4497" />
                                <Input label="Breast Feeding" value={slip.infant.breastFeeding} onChange={e => updateInfant({
                          breastFeeding: e.target.value
                        })} data-id="element-4498" />
                                <Input label="BCG" value={slip.infant.bcg} onChange={e => updateInfant({
                          bcg: e.target.value
                        })} data-id="element-4499" />
                                <Input label="OPV" value={slip.infant.opv} onChange={e => updateInfant({
                          opv: e.target.value
                        })} data-id="element-4500" />
                                <Input label="Birth Record" value={slip.infant.birthRecord} onChange={e => updateInfant({
                          birthRecord: e.target.value
                        })} data-id="element-4501" />
                                <Input label="G/C" value={slip.infant.gc} onChange={e => updateInfant({
                          gc: e.target.value
                        })} data-id="element-4502" />
                                <div className="md:col-span-2" data-id="element-4503">
                                  <Input label="Treatment" value={slip.infant.treatment} onChange={e => updateInfant({
                            treatment: e.target.value
                          })} data-id="element-4504" />
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4505">
                              <Input label="6 days Postnatal Date" type="date" value={slip.sixDaysPostnatalDate} onChange={e => updateSlip({
                        sixDaysPostnatalDate: e.target.value
                      })} data-id="element-4506" />
                              <Input label="6 Weeks Postnatal Date" type="date" value={slip.sixWeeksPostnatalDate} onChange={e => updateSlip({
                        sixWeeksPostnatalDate: e.target.value
                      })} data-id="element-4507" />
                              <div className="md:col-span-2" data-id="element-4508">
                                <Input label="Name in Full" value={slip.nameInFull} onChange={e => updateSlip({
                          nameInFull: e.target.value
                        })} data-id="element-4509" />
                              </div>
                            </div>
                          </div>
                        </div>;
              })()}
                  </Card>

                  {(() => {
              const defaultVisit = {
                date: '',
                mother: {
                  generalWellBeing: '',
                  temp: '',
                  pulse: '',
                  bp: '',
                  urine: '',
                  head: '',
                  eyes: '',
                  ears: '',
                  mouth: '',
                  neck: '',
                  breasts: '',
                  ampills: '',
                  abdomenUterus: '',
                  groin: '',
                  perineum: '',
                  legs: '',
                  anus: '',
                  lochia: '',
                  cervixPerSpeculum: '',
                  familyPlanning: '',
                  treatmentOrAdvise: ''
                },
                infant: {
                  name: '',
                  weight: '',
                  date: '',
                  headCircumference: '',
                  length: '',
                  temp: '',
                  head: '',
                  eyes: '',
                  ears: '',
                  mouth: '',
                  neck: '',
                  chest: '',
                  abdomen: '',
                  cord: '',
                  genitaliaRectum: '',
                  extremities: '',
                  back: '',
                  neurologicExam: '',
                  feeding: '',
                  additionalNotes: ''
                }
              };
              const motherFieldOrder: Array<[keyof typeof defaultVisit.mother, string]> = [['generalWellBeing', 'General Well Being'], ['temp', 'Temp'], ['pulse', 'Pulse'], ['bp', 'BP'], ['urine', 'Urine'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['breasts', 'Breasts'], ['ampills', 'Ampills'], ['abdomenUterus', 'Abdomen (Uterus)'], ['groin', 'Groin'], ['perineum', 'Perineum'], ['legs', 'Legs'], ['anus', 'Anus'], ['lochia', 'Lochia']];
              const infantFieldOrder: Array<[keyof typeof defaultVisit.infant, string]> = [['headCircumference', 'Head Circumference'], ['length', 'Length'], ['temp', 'Temp'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['chest', 'Chest'], ['abdomen', 'Abdomen'], ['cord', 'Cord'], ['genitaliaRectum', 'Genitalia & Rectum'], ['extremities', 'Extremities'], ['back', 'Back'], ['neurologicExam', 'Neurologic Exam'], ['feeding', 'Feeding']];
              const renderVisitForm = (visitKey: 'postNatalOneWeek' | 'postNatalSixWeeks') => {
                const visit = (selectedMaternityFile as any)[visitKey] || defaultVisit;
                const updateVisit = (patch: Partial<typeof defaultVisit>) => {
                  setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    [visitKey]: {
                      ...visit,
                      ...patch
                    }
                  });
                };
                const updateMother = (patch: Partial<typeof defaultVisit.mother>) => {
                  updateVisit({
                    mother: {
                      ...visit.mother,
                      ...patch
                    }
                  });
                };
                const updateInfant = (patch: Partial<typeof defaultVisit.infant>) => {
                  updateVisit({
                    infant: {
                      ...visit.infant,
                      ...patch
                    }
                  });
                };
                return <div className="space-y-6" data-id="element-4510">
                          <Input label="Date" type="date" value={visit.date} onChange={e => updateVisit({
                    date: e.target.value
                  })} data-id="element-4511" />

                          <div className="bg-pink-50 p-3 rounded" data-id="element-4512">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4513">
                              Mother
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4514">
                              {motherFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.mother[key]} onChange={e => updateMother({
                        [key]: e.target.value
                      } as any)} data-id="element-4515" />)}
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-4516">
                              <h6 className="font-semibold text-sm uppercase" data-id="element-4517">
                                Vaginal Examination
                              </h6>
                              <div data-id="element-4518">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4519">
                                  Cervix per speculum
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.cervixPerSpeculum} onChange={e => updateMother({
                          cervixPerSpeculum: e.target.value
                        })} data-id="element-4520" />
                              </div>
                              <div data-id="element-4521">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4522">
                                  Family Planning
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.familyPlanning} onChange={e => updateMother({
                          familyPlanning: e.target.value
                        })} data-id="element-4523" />
                              </div>
                              <div data-id="element-4524">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4525">
                                  Treatment or Advise
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.mother.treatmentOrAdvise} onChange={e => updateMother({
                          treatmentOrAdvise: e.target.value
                        })} data-id="element-4526" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 p-3 rounded" data-id="element-4527">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4528">
                              Infant Examination Form
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-4529">
                              <Input label="Infant Name" value={visit.infant.name} onChange={e => updateInfant({
                        name: e.target.value
                      })} data-id="element-4530" />
                              <Input label="Weight" value={visit.infant.weight} onChange={e => updateInfant({
                        weight: e.target.value
                      })} data-id="element-4531" />
                              <Input label="Date" type="date" value={visit.infant.date} onChange={e => updateInfant({
                        date: e.target.value
                      })} data-id="element-4532" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4533">
                              {infantFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.infant[key]} onChange={e => updateInfant({
                        [key]: e.target.value
                      } as any)} data-id="element-4534" />)}
                            </div>
                            <div className="mt-3" data-id="element-4535">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4536">
                                Additional Notes
                              </label>
                              <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.infant.additionalNotes} onChange={e => updateInfant({
                        additionalNotes: e.target.value
                      })} data-id="element-4537" />
                            </div>
                          </div>
                        </div>;
              };
              return <>
                        <Card title="Postnatal Visit — One Week" data-id="element-4538">
                          {renderVisitForm('postNatalOneWeek')}
                        </Card>
                        <Card title="Postnatal Visit — Six Weeks" data-id="element-4539">
                          {renderVisitForm('postNatalSixWeeks')}
                        </Card>
                      </>;
            })()}
                </div>
              </MaternityErrorBoundary> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center" data-id="element-4540">
                <Baby className="w-16 h-16 text-gray-300 mb-4" data-id="element-4541" />
                <p className="text-lg font-medium text-gray-900" data-id="element-4542">
                  Select a maternity file
                </p>
                <p data-id="element-4543">
                  Choose a patient from the list to view and update their
                  maternity records.
                </p>
              </div>}
          </div>
        </div>}
    </div>;
};